"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Problemstatement() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".mem-left > *", {
        y: 24
      });
      gsap.to(".mem-left > *", {
        opacity: 1,
        y: 0,
        duration: 0.62,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".mem-image", {
        y: 32
      });
      gsap.to(".mem-image", {
        opacity: 1,
        y: 0,
        duration: 0.72,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".mem-dot", {
        scale: 0
      });
      gsap.to(".mem-dot", {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        stagger: 0.12,
        ease: "back.out(2)",
        scrollTrigger: {
          trigger: el,
          start: "top 70%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".mem-left > *", ".mem-image", ".mem-dot"], {
        opacity: 1,
        y: 0,
        scale: 1
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  const features = [{
    label: "Precision",
    desc: "Engineered systems that ensure crystal clear water quality."
  }, {
    label: "Reliable",
    desc: "Technical expertise for every repair and routine maintenance."
  }, {
    label: "Modern",
    desc: "Advanced renovation techniques for a sleek, updated look."
  }];
  return <section data-section-id="28307" id="problemstatement" data-nav="light" ref={container} className="bg-background text-foreground py-28 md:py-40 overflow-hidden">
      <div className="mx-auto max-w-[1360px] px-5 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div className="mem-left lg:col-span-6 order-2 lg:order-1 flex flex-col">
            <span className="font-serif text-xs md:text-sm font-medium uppercase tracking-[0.14em] text-muted-foreground" style={{
            opacity: 0
          }}>
              {"The pool standard"}
            </span>

            <h2 className="font-serif font-normal text-foreground mt-5 text-[clamp(2.25rem,4.8vw,4rem)] leading-[1.02] tracking-[-0.015em]" style={{
            opacity: 0
          }}>
              {"Every pool is a "}
              <em className="not-italic font-serif italic font-normal" style={{
              color: "var(--primary)"
            }}>
                {"masterpiece."}
              </em>
            </h2>

            <p className="mt-8 text-foreground/85 text-[clamp(1.0625rem,1.3vw,1.25rem)] leading-[1.55] max-w-[50ch]" style={{
            opacity: 0
          }}>
              {"Rocket City Water Wizards delivers technical excellence in every installation, repair, and renovation. We combine precision engineering with a commitment to reliability, ensuring your pool remains a pristine, high-performance asset for years to come."}
            </p>

            <ul className="mt-10 flex flex-col gap-6" style={{
            opacity: 0
          }}>
              {features.map((f, i) => <li key={f.label} data-index={i} className="flex items-start gap-4">
                  <span className="mt-2 inline-block h-2.5 w-2.5 rounded-lg flex-shrink-0" style={{
                backgroundColor: "var(--primary)"
              }} aria-hidden="true" />
                  <div>
                    <h3 className="font-default text-foreground text-[clamp(1.125rem,1.6vw,1.375rem)] font-semibold tracking-[-0.005em] leading-[1.25]">
                      {f.label}
                    </h3>
                    <p className="mt-1 text-muted-foreground text-base leading-[1.55]">
                      {f.desc}
                    </p>
                  </div>
                </li>)}
            </ul>
          </div>

          {}
          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <div className="relative flex flex-col items-end gap-6">
              {}
              <div className="mem-image relative w-full max-w-[560px] overflow-hidden bg-secondary" style={{
              opacity: 0,
              height: "clamp(340px, 52vw, 500px)",
              borderRadius: "9999px"
            }}>
                <img src="https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/rcww-inspection-diagnostic-e1785980744240.jpg" data-wvc-srcset="https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/rcww-inspection-diagnostic-e1785980744240.jpg-wvc-srcset" data-wvc-sizes="https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/rcww-inspection-diagnostic-e1785980744240.jpg-wvc-sizes" alt="Modern pool installation site showing clean lines and professional equipment" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </div>

              {}
              <div className="flex items-center gap-6 md:gap-10 self-end pr-4 md:pr-16">
                <div className="flex flex-col gap-3">
                  {[0, 1, 2].map(i => <span key={i} data-index={i} className="mem-dot inline-block h-3 w-3 rounded-lg" style={{
                  backgroundColor: "var(--primary)",
                  opacity: 0
                }} aria-hidden="true" />)}
                </div>

                <div className="mem-image relative overflow-hidden bg-secondary flex-shrink-0" style={{
                opacity: 0,
                width: "clamp(180px, 26vw, 240px)",
                height: "clamp(180px, 26vw, 240px)",
                borderRadius: "9999px"
              }}>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_maintenance_detail.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_maintenance_detail.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_maintenance_detail.png-wvc-sizes" alt="Close up of professional pool maintenance equipment and crystal clear water" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}